import React from 'react';
import { 
  Package, DollarSign, Handshake, Trophy, Calendar, User,
  Briefcase, Rocket, Building2, Megaphone
} from 'lucide-react';

export const PR_TEMPLATES = [
  {
    id: 'product_launch',
    name: 'Product Launch',
    icon: Package,
    color: 'bg-blue-500',
    description: 'Announce a new product or feature',
    title: '[Company Name] Launches [Product Name] - [Key Benefit]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name], a leading [industry] company, today announced the launch of [Product Name], a [brief description of what it is].</p>

<h3>Key Highlights:</h3>
<ul>
<li>[Key feature/benefit 1]</li>
<li>[Key feature/benefit 2]</li>
<li>[Key feature/benefit 3]</li>
</ul>

<p>[Product Name] addresses [problem it solves] by [how it solves it]. With [unique selling proposition], it stands out from existing solutions in the market.</p>

<blockquote>
<p>"[Quote about the product launch and its significance]"</p>
<p>— [Spokesperson Name], [Title], [Company Name]</p>
</blockquote>

<h3>Availability & Pricing</h3>
<p>[Product Name] is available [availability details] at [pricing information]. [Any launch offers or promotions].</p>

<h3>About [Company Name]</h3>
<p>[2-3 sentence company boilerplate describing what the company does, when it was founded, and any notable achievements].</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'funding_announcement',
    name: 'Funding Announcement',
    icon: DollarSign,
    color: 'bg-green-500',
    description: 'Announce funding round or investment',
    title: '[Company Name] Raises ₹[Amount] in [Round] Funding Led by [Lead Investor]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name], a [brief description], today announced it has raised ₹[Amount] in [Seed/Series A/B/C] funding. The round was led by [Lead Investor], with participation from [Other Investors].</p>

<h3>Use of Funds</h3>
<p>The fresh capital will be used to:</p>
<ul>
<li>[Primary use - e.g., Expand team and hire key talent]</li>
<li>[Secondary use - e.g., Scale operations to new markets]</li>
<li>[Tertiary use - e.g., Invest in product development]</li>
</ul>

<blockquote>
<p>"[Quote from Founder/CEO about the funding and future plans]"</p>
<p>— [Founder Name], [Title], [Company Name]</p>
</blockquote>

<blockquote>
<p>"[Quote from Lead Investor about why they invested]"</p>
<p>— [Investor Name], [Title], [Investment Firm]</p>
</blockquote>

<h3>Company Traction</h3>
<p>[Include key metrics, growth numbers, customer base, or other relevant traction points]</p>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>

<h3>About [Lead Investor]</h3>
<p>[Brief description of the lead investor/VC firm]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'partnership',
    name: 'Partnership',
    icon: Handshake,
    color: 'bg-purple-500',
    description: 'Announce strategic partnership or collaboration',
    title: '[Company A] Partners with [Company B] to [Outcome/Goal]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company A] and [Company B] today announced a strategic partnership to [brief description of partnership goal/outcome].</p>

<h3>Partnership Details</h3>
<p>Under this partnership, [explain what each company brings to the table and how they will work together].</p>

<h3>Key Benefits</h3>
<ul>
<li>[Benefit for customers/market 1]</li>
<li>[Benefit for customers/market 2]</li>
<li>[Benefit for customers/market 3]</li>
</ul>

<blockquote>
<p>"[Quote from Company A representative about the partnership]"</p>
<p>— [Name], [Title], [Company A]</p>
</blockquote>

<blockquote>
<p>"[Quote from Company B representative about the partnership]"</p>
<p>— [Name], [Title], [Company B]</p>
</blockquote>

<h3>About [Company A]</h3>
<p>[Company A boilerplate]</p>

<h3>About [Company B]</h3>
<p>[Company B boilerplate]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'award_recognition',
    name: 'Award & Recognition',
    icon: Trophy,
    color: 'bg-yellow-500',
    description: 'Announce awards, rankings, or certifications',
    title: '[Company Name] Recognized as [Award Name] by [Awarding Body]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name] has been recognized as [Award/Recognition] by [Awarding Organization]. This recognition highlights [what the award recognizes].</p>

<h3>About the Award</h3>
<p>[Description of the award, its significance, selection criteria, and the awarding organization].</p>

<h3>Why [Company Name] Was Selected</h3>
<p>[Explain the specific achievements or qualities that led to this recognition]:</p>
<ul>
<li>[Achievement/Quality 1]</li>
<li>[Achievement/Quality 2]</li>
<li>[Achievement/Quality 3]</li>
</ul>

<blockquote>
<p>"[Quote expressing gratitude and the significance of this recognition]"</p>
<p>— [Spokesperson Name], [Title], [Company Name]</p>
</blockquote>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'event_coverage',
    name: 'Event Coverage',
    icon: Calendar,
    color: 'bg-red-500',
    description: 'Announce or cover company events',
    title: '[Company Name] to Host/Participate in [Event Name] on [Date]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name] announces [its participation in / hosting of] [Event Name], scheduled for [Date] at [Venue/Platform].</p>

<h3>Event Details</h3>
<ul>
<li><strong>Event:</strong> [Event Name]</li>
<li><strong>Date:</strong> [Date and Time]</li>
<li><strong>Venue:</strong> [Physical location or virtual platform]</li>
<li><strong>Theme:</strong> [Event theme if applicable]</li>
</ul>

<h3>What to Expect</h3>
<p>[Description of event activities, speakers, sessions, or highlights]:</p>
<ul>
<li>[Highlight 1]</li>
<li>[Highlight 2]</li>
<li>[Highlight 3]</li>
</ul>

<blockquote>
<p>"[Quote about the event and what attendees can expect]"</p>
<p>— [Spokesperson Name], [Title], [Company Name]</p>
</blockquote>

<h3>Registration</h3>
<p>[How to register, ticket prices if any, and registration deadline]</p>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'founder_story',
    name: 'Founder Story',
    icon: User,
    color: 'bg-indigo-500',
    description: 'Share founder journey or interview',
    title: 'From [Origin Story] to [Current Achievement]: The [Founder Name] Story',
    content: `<h2>[Compelling Headline About the Founder's Journey]</h2>

<p><strong>[City]</strong> — [Opening hook that captures attention - an interesting fact, challenge, or achievement about the founder].</p>

<h3>The Beginning</h3>
<p>[Share the founder's background, what they were doing before starting the company, and what inspired them to take the entrepreneurial leap].</p>

<h3>The Challenge</h3>
<p>[Describe the problem they identified and the challenges they faced in the early days]:</p>
<ul>
<li>[Challenge 1 and how they overcame it]</li>
<li>[Challenge 2 and how they overcame it]</li>
</ul>

<h3>The Solution</h3>
<p>[How they built [Company Name] to solve the problem, key decisions they made, and pivotal moments in the journey].</p>

<blockquote>
<p>"[Inspirational quote from the founder about their journey, learnings, or vision]"</p>
<p>— [Founder Name], [Title], [Company Name]</p>
</blockquote>

<h3>Where They Are Today</h3>
<p>[Current achievements, team size, customer base, funding, or other relevant metrics].</p>

<h3>Vision for the Future</h3>
<p>[What's next for the founder and the company].</p>

<h3>Advice for Aspiring Entrepreneurs</h3>
<blockquote>
<p>"[Quote with advice for others on a similar journey]"</p>
<p>— [Founder Name]</p>
</blockquote>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>`
  },
  {
    id: 'expansion',
    name: 'Expansion News',
    icon: Building2,
    color: 'bg-teal-500',
    description: 'Announce geographic or market expansion',
    title: '[Company Name] Expands to [New Market/Location] to [Goal]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name] today announced its expansion into [New Market/City/Country], marking a significant milestone in the company's growth journey.</p>

<h3>Expansion Details</h3>
<p>[Details about the new market, what operations will be established, and timeline].</p>

<h3>Why [New Market]</h3>
<ul>
<li>[Reason 1 - market opportunity, demand, etc.]</li>
<li>[Reason 2 - strategic importance]</li>
<li>[Reason 3 - customer base or partnerships]</li>
</ul>

<h3>Investment & Jobs</h3>
<p>[Details about investment being made, new office if any, and job creation plans].</p>

<blockquote>
<p>"[Quote about the expansion and what it means for the company]"</p>
<p>— [Spokesperson Name], [Title], [Company Name]</p>
</blockquote>

<h3>Current Presence</h3>
<p>[Brief overview of company's current geographic presence and customer base].</p>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'milestone',
    name: 'Company Milestone',
    icon: Rocket,
    color: 'bg-orange-500',
    description: 'Announce achievements or milestones',
    title: '[Company Name] Achieves [Milestone] - [Context/Significance]',
    content: `<h2>FOR IMMEDIATE RELEASE</h2>

<p><strong>[City, Date]</strong> — [Company Name] today announced that it has achieved [Milestone - e.g., 1 million users, ₹100 crore revenue, 10,000 customers], marking a significant achievement in the company's [X year] journey.</p>

<h3>The Journey to [Milestone]</h3>
<p>[Brief timeline or story of how the company reached this milestone]:</p>
<ul>
<li><strong>[Year 1]:</strong> [Key event]</li>
<li><strong>[Year 2]:</strong> [Key event]</li>
<li><strong>[Current]:</strong> [Milestone achieved]</li>
</ul>

<h3>What This Means</h3>
<p>[Context about the significance of this milestone in the industry or market].</p>

<blockquote>
<p>"[Quote celebrating the milestone and thanking customers/team]"</p>
<p>— [Spokesperson Name], [Title], [Company Name]</p>
</blockquote>

<h3>What's Next</h3>
<p>[Future goals and plans following this milestone].</p>

<h3>About [Company Name]</h3>
<p>[Company boilerplate]</p>

<p><strong>Media Contact:</strong><br/>
[Contact Name]<br/>
[Email]<br/>
[Phone]</p>`
  },
  {
    id: 'blank',
    name: 'Blank Template',
    icon: Briefcase,
    color: 'bg-gray-500',
    description: 'Start from scratch',
    title: '',
    content: ''
  }
];

const PRTemplateSelector = ({ onSelect, selectedTemplate }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
      {PR_TEMPLATES.map((template) => {
        const Icon = template.icon;
        const isSelected = selectedTemplate?.id === template.id;
        
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template)}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              isSelected 
                ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                : 'border-[#e5e5e5] hover:border-[#D4AF37]/50'
            }`}
          >
            <div className={`w-10 h-10 ${template.color} rounded-lg flex items-center justify-center mb-3`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-manrope text-sm font-semibold text-[#1a1a1a] mb-1">
              {template.name}
            </h4>
            <p className="font-manrope text-xs text-[#737373] line-clamp-2">
              {template.description}
            </p>
          </button>
        );
      })}
    </div>
  );
};

export default PRTemplateSelector;
